import React, { forwardRef, useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { translate } from '../../locales';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import SelectMenu from '../SelectMenu/SelectMenu';
import Typography from '../Typography/Typography';
import useIsTextOverflow from '../../customHooks/useIsTextOverflow';
import ConditionalTooltip from '../../helpers/ConditionalTooltip';
import BaseSelectButton from '../BaseSelectButton/BaseSelectButton';
import { useMultipleRefsSetter } from '../../customHooks/useMultipleRefsSetter';
import { selectClasses as classes } from './selectClasses';
import { IconContainer, LabelContainer, SelectRoot } from './Select.style';

const Select = forwardRef(
  (
    {
      options,
      value,
      onChange,
      onInputChange,
      label,
      placeholder = translate('common.select'),
      disabled,
      readOnly = false,
      FormLabelProps,
      TooltipProps,
      size = 'medium',
      errorText,
      helperText,
      SelectMenuProps,
      className,
      onClick,
      maxOptions = 100,
      renderButtonLabel,
      creatable,
      onCreate,
      shouldCreateNewOption,
      isClearable,
      isLoading = false,
      async = false,
      onOpen,
      onClose,
      sx,
      ...rest
    },
    externalRef
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const anchorRef = useRef(null);
    const rootRefCallback = useMultipleRefsSetter(externalRef, anchorRef);
    const isError = !isEmpty(errorText);
    const hasValue = !isEmpty(value);
    const hasAppIcon = hasValue && typeof value.renderIcon === 'function';
    const shouldRenderClearIcon =
      hasValue && isClearable && !disabled && !readOnly;
    const [
      shouldShowLabelTooltip,
      itemLabelValueElementRef
    ] = useIsTextOverflow(value);

    const handleToggleMenu = useCallback(
      shouldOpen => {
        setIsOpen(shouldOpen);
        if (onOpen && shouldOpen) {
          onOpen();
        }
        if (onClose && !shouldOpen) {
          onClose();
        }
      },
      [onOpen, onClose]
    );

    const handleButtonClick = useCallback(
      event => {
        onClick && onClick(event);
        handleToggleMenu(true);
      },
      [onClick, handleToggleMenu]
    );
    const clearSelection = !disabled
      ? event => {
          event.stopPropagation();
          event.preventDefault();
          onChange(null);
        }
      : undefined;

    const ExpandIcon = isOpen ? ExpandLess : ExpandMore;
    return (
      <SelectRoot className={className} sx={sx}>
        <BaseSelectButton
          isError={isError}
          hasValues={hasValue}
          readOnly={readOnly}
          label={label}
          FormLabelProps={FormLabelProps}
          TooltipProps={TooltipProps}
          clearSelection={clearSelection}
          shouldRenderClearIcon={shouldRenderClearIcon}
          ExpandIcon={ExpandIcon}
          errorText={errorText}
          helperText={helperText}
          disabled={disabled}
          ref={rootRefCallback}
          data-value={value?.value}
          onClick={!readOnly ? handleButtonClick : undefined}
          size={size}
          {...rest}>
          <LabelContainer
            item
            container
            className={classes.labelWrapper}
            hasAppIcon={hasAppIcon}>
            {!!renderButtonLabel ? (
              renderButtonLabel({
                className: classes.selectButtonText,
                ref: itemLabelValueElementRef,
                value,
                options,
                placeholder,
                isTextOverflow: shouldShowLabelTooltip
              })
            ) : (
              <>
                {hasAppIcon && (
                  <IconContainer className={classes.appIcon}>
                    {value.renderIcon({
                      size: 'small'
                    })}
                  </IconContainer>
                )}
                <ConditionalTooltip
                  hide={!value || !shouldShowLabelTooltip}
                  title={value?.label}
                  placement="bottom">
                  <Typography
                    data-qa-id="select-value"
                    className={classes.selectButtonText}
                    ref={itemLabelValueElementRef}
                    align="left"
                    variant="body1">
                    {value?.label || placeholder}
                  </Typography>
                </ConditionalTooltip>
              </>
            )}
          </LabelContainer>
        </BaseSelectButton>
        <SelectMenu
          {...{
            isOpen,
            setIsOpen: handleToggleMenu,
            anchorRef,
            options,
            value,
            onChange,
            onInputChange,
            size,
            creatable,
            onCreate,
            shouldCreateNewOption,
            maxOptions,
            isLoading,
            async,
            ...SelectMenuProps
          }}
        />
      </SelectRoot>
    );
  }
);

Select.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any,
      /** Support groups */
      groupName: PropTypes.string
    })
  ),
  value: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.any,
    /** Support groups */
    groupName: PropTypes.string
  }),
  /**
   * A function that triggered when a change occurred
   *
   * **Signature**
   *
   * `function(selectedOption: object | null) => void`
   *
   * */
  onChange: PropTypes.func.isRequired,
  onInputChange: PropTypes.func,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  /** Props passed to the FormLabel component */
  FormLabelProps: PropTypes.object,
  /** If passed, a Tooltip will be rendered next to the label and these props will be passed to that Tooltip. */
  TooltipProps: PropTypes.object,
  maxOptions: PropTypes.number,
  isLoading: PropTypes.bool,
  async: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large', 'fullWidth']),
  creatable: PropTypes.bool,
  onCreate: PropTypes.func,
  /** A function to determine if a new option should be created
   * (input, options) => boolean
   */
  shouldCreateNewOption: PropTypes.func,
  errorText: PropTypes.string,
  helperText: PropTypes.string,
  SelectMenuProps: PropTypes.object,
  renderButtonLabel: PropTypes.func,
  isClearable: PropTypes.bool,
  /**
   * Triggered when the menu is opening
   *
   * **Signature**
   *
   * `function() => void`
   *
   * */
  onOpen: PropTypes.func,
  /**
   * Triggered when the menu is closing
   *
   * **Signature**
   *
   * `function() => void`
   *
   * */
  onClose: PropTypes.func
};

Select.classes = classes;
Select.displayName = 'Select';

export default Select;
